import React, { memo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, func, node, string, bool } from 'prop-types'

import SvgIcon from 'Pods/Shared/SvgIcon'
import Community from 'Pods/UnifiedNavigation/Icons/Community'
import Jobs from 'Pods/UnifiedNavigation/Icons/Jobs'
import Models from 'Pods/UnifiedNavigation/Icons/Models'

import useDelayLoad from 'Utils/useDelayLoad'

function MenuButton({
  menuItem,
  activeDropdown = null,
  setActiveDropdown,
  dropdownComponent = null,
  showIcon = false,
  extractingCoreStyles = false,
  isDropdownLoaded = false,
  hasDropdown = false,
  onWidgetLoad = null,
}) {
  const { id, href, title } = menuItem
  const { isLoaded, elementRef, loadContent } = useDelayLoad()

  const iconMap = {
    'navigation-3d-models': Models,
    'navigation-jobs': Jobs,
    'navigation-community': Community,
  }

  const IconComponent = iconMap[id] || null
  const WrapperComponent = href ? 'a' : 'div'

  function handleButtonClick(e) {
    if (hasDropdown) {
      e.preventDefault()
      // Open the render gate before toggling/importing the dropdown on first interaction.
      loadContent()

      if (activeDropdown === id) {
        return setActiveDropdown(null)
      }

      if (onWidgetLoad && !isDropdownLoaded) {
        onWidgetLoad(id, () => {
          setActiveDropdown(id)
        })
      } else {
        setActiveDropdown(id)
      }

      return null
    }

    return null
  }

  function handleMouseEnter() {
    loadContent()

    if (hasDropdown && onWidgetLoad && !isDropdownLoaded) {
      onWidgetLoad(id)
    }
  }

  function handleTouchStart() {
    loadContent()

    if (hasDropdown && onWidgetLoad && !isDropdownLoaded) {
      onWidgetLoad(id)
    }
  }

  return (
    <li
      id={id}
      className={classnames(
        'app-nav__item js-gtm-top-site-subnav js-subnav-toggle',
        activeDropdown === id && 'is-subnav-active'
      )}
      ref={elementRef}
      onMouseEnter={hasDropdown ? handleMouseEnter : undefined}
      onTouchStart={hasDropdown ? handleTouchStart : undefined}
    >
      <WrapperComponent
        className={classnames(
          'cgt-button',
          'cgt-button--secondary',
          'cgt-button--medium',
          'cgt-button--with-icon',
          'app-nav__item-link',
          'cgt-button--rectangular'
        )}
        {...{ href }}
        data-track-name={title}
      >
        <button
          className={classnames(
            'cgt-button',
            'cgt-button--ghost',
            'cgt-button--medium',
            'cgt-button--only-icon',
            'app-nav__trigger-mobile'
          )}
          onClick={handleButtonClick}
          aria-label={`Toggle ${title} dropdown`}
        >
          <div className={classnames('cgt-icon', 'cgt-icon__icon', 'cgt-icon--default', 'cgt-icon--primary')}>
            <i className="custom-font-icon custom-icon--light icon-arrow-right" />
          </div>
        </button>

        {showIcon && IconComponent ? (
          <SvgIcon>
            <IconComponent />
          </SvgIcon>
        ) : null}

        <span className="app-nav__item-name uppercased-text">{title}</span>
      </WrapperComponent>
      {(isLoaded || extractingCoreStyles) && dropdownComponent}
    </li>
  )
}

MenuButton.propTypes = {
  dropdownComponent: node,
  menuItem: shape({
    id: string.isRequired,
    href: string,
    title: string.isRequired,
  }).isRequired,
  activeDropdown: string,
  setActiveDropdown: func.isRequired,
  showIcon: bool,
  extractingCoreStyles: bool,
  isDropdownLoaded: bool,
  hasDropdown: bool,
  onWidgetLoad: func,
}

export default memo(MenuButton)
